import type { NormalizedAssetData } from './asset-tab-types';

// --- Asset Detail API ---

export interface AssetClassification {
    primary_type: string;
    sub_categories: string[];
    sector?: string;
    industry?: string;
}

export interface MacroSensitivity {
    indicator: string;
    direction: 'positive' | 'negative';
    strength: 'HIGH' | 'MEDIUM' | 'LOW';
    explanation?: string;
}

export interface AssetDetail {
    ticker: string;
    asset_name: string;
    asset_type: string;
    classification: AssetClassification;
    description: {
        short_summary: string;
        long_summary?: string;
    };
    market_drivers: {
        macro_sensitivities: MacroSensitivity[];
    };
    market_structure?: {
        issuer?: string;
        exchange?: string;
        market_cap?: string;
    };
    data?: NormalizedAssetData;
}

// --- Markets from Asset API ---

export interface MarketFromAsset {
    event_id?: string;
    condition_id: string;
    question: string;
    slug?: string;
    similarity: number;
    volume?: number;
    current_yes_prob?: number;
    delta_7d?: number;
    start_date?: string;
    end_date?: string;
    resolved?: boolean;
    outcome?: string;
    active?: boolean;
}

export interface MarketsFromAssetResponse {
    ticker: string;
    asset_name: string;
    markets: MarketFromAsset[];
}

// --- Events from Asset API ---

export interface EventMarketSummary {
    condition_id: string;
    question: string;
    yes_prob: number;
    yes_outcome: string;
    volume: number;
    volume_share: number;
    image?: string;
}

export interface EventFromAsset {
    event_id: string;
    event_title: string;
    similarity: number;
    related_tickers: string[];
    asset_types: string[];
    tags: string[];
    slug: string;
    start_date: string;
    end_date: string;
    volume: number;
    market_count: number;
    market_type: string;
    markets_summary: EventMarketSummary[];
    image?: string;
}

export interface EventsFromAssetResponse {
    ticker: string;
    asset_name: string;
    events: EventFromAsset[];
}

// --- Assets from Market API ---

export interface AssetFromMarket {
    ticker: string;
    name: string;
    asset_type: string;
    similarity: number;
}

export interface AssetsFromMarketResponse {
    condition_id: string;
    assets: AssetFromMarket[];
}

// --- Asset Price API ---

export interface AssetPriceParams {
    ticker: string;
    startDate: string; // ISO date e.g. "2024-01-01"
    endDate: string;
    frequency: '12hour' | '1hour';
}

export interface PriceRow {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
}

export interface CryptoAssetPriceEntry {
    ticker: string;
    baseCurrency: string;
    quoteCurrency: string;
    priceData: PriceRow[];
}

// --- Chart Data Types ---

export interface OHLCVPoint {
    t: Date;
    open: number;
    high: number;
    low: number;
    close: number;
    vol: number;
}

export interface ProbabilityPoint {
    t: Date;
    prob: number;
}

// --- Event Markets API (GET /api/event-markets/{event_id}) ---

export interface EventMarketDetail {
    condition_id: string;
    question: string;
    outcomes: string[] | null;
    outcome_prices: number[] | null;
    token0_id: string | null;
    token1_id: string | null;
    volume: number | null;
    current_yes_prob: number | null;
    start_date: string | null;
    end_date: string | null;
    closed: boolean | null;
    market_type: string | null;
    image: string | null;
    yes_outcome: string | null;
}

export interface EventMarketsResponse {
    event_id: string;
    event_title: string | null;
    slug: string | null;
    markets: EventMarketDetail[];
    tags: string[];
    start_date: string | null;
    end_date: string | null;
    total_volume: number | null;
    event_image: string | null;
    description: string | null;
    neg_risk: boolean;
}

// ---------------------------------------------------------------------------
// Signal types — mirrors api_signals.py response models
// ---------------------------------------------------------------------------

// Trader group classification
export type SignalTraderGroup = 'insider' | 'winner' | 'whale' | 'none';

// Signal lifecycle
export type SignalLifecycle = 'ephemeral' | 'position_bound';

// Signal status
export type SignalStatus = 'open' | 'closed';

// Signal direction
export type SignalDirection = 'bullish' | 'bearish' | null;

// The four structured content components on a signal
export interface SignalFact {
    [key: string]: unknown;
}

export interface SignalSignificance {
    [key: string]: unknown;
}

export interface SignalValidation {
    [key: string]: unknown;
}

export interface SignalInterpretation {
    [key: string]: unknown;
}

export interface SignalSession {
    [key: string]: unknown;
}

// Single signal — mirrors SignalItem in api_signals.py
export interface SignalItem {
    id: string;
    opened_at: string; // ISO 8601
    closed_at: string | null; // ISO 8601, null if still open
    last_seen_at: string | null; // ISO 8601

    market_id: string;
    market_question: string;

    signal_type: string; // e.g. 'position_opened', 'probability_spike'
    trader_group: SignalTraderGroup;
    status: SignalStatus;
    lifecycle: SignalLifecycle;
    direction: SignalDirection;
    score: number;

    // Only present for trader-originating signals
    address: string | null;
    address_alias: string | null;

    // Structured content components
    fact: SignalFact;
    significance: SignalSignificance;
    validation: SignalValidation;
    interpretation: SignalInterpretation;

    // Session summary
    session: SignalSession;
}

// Response envelope from GET /api/signals/{market_id}
export interface MarketSignalsResponse {
    market_id: string;
    signals: SignalItem[];
    signal_count: number;
    open_signal_count: number;
    has_more: boolean;
}

// ---------------------------------------------------------------------------
// Overlay type — passed from EventView into ConvergenceChart (future use)
// ---------------------------------------------------------------------------

export interface SignalOverlayData {
    // All signals keyed by conditionId (market_id)
    byMarket: Map<string, SignalItem[]>;
    // Reuse the existing market color map so signal markers match their line
    colorMap: Map<string, string>;
}

// Signal types that belong in the top (ConvergenceChart) — market-level
export const MARKET_SIGNAL_TYPES = new Set([
    'probability_spike',
    'probability_change',
    'volume_anomaly',
    'activity_surge',
    'buy_sell_imbalance',
    'large_trade',
]);

// ---------------------------------------------------------------------------
// Alert types — mirrors api_signals.py AlertItem / AlertsResponse models
// ---------------------------------------------------------------------------

export type AlertSeverity = 'log' | 'info' | 'warning' | 'critical';

export interface AlertItem {
    id: string;
    ts: string; // ISO 8601
    event_id: string | null;
    market_id: string;
    current_yes_prob: number;
    alert_type: string;
    severity: AlertSeverity;
    title: string;
    detail: string | null;
    context: Record<string, unknown>;
    /** True when the alert was produced from simulated data; only delivered to
     *  users with the `simulation` feature. Absent/false for real alerts. */
    is_simulated?: boolean;
    source_ids: string[];
}

// Passed from EventView into ConvergenceChart
export interface AlertsOverlayData {
    alerts: AlertItem[];
}
